import {
  Card,
  CardHeader,
  makeStyles,
  tokens,
  Text,
  Body2,
  Caption1,
  Badge,
  Button,
  Divider,
} from '@fluentui/react-components';
import { EyeOffRegular, ArrowRightRegular } from '@fluentui/react-icons';
import type { RuntimeBlindSpotsSummary } from '../types';
import { getScoreColor, getSeverityBadgeColor, formatBlindSpotType } from '../utils/scoring';

interface RuntimeBlindSpotsCardProps {
  summary: RuntimeBlindSpotsSummary;
  onViewAll: () => void;
}

const useStyles = makeStyles({
  scoreSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    marginBottom: tokens.spacingVerticalS,
  },
  severityCounts: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  topItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
  },
  topItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalS} 0`,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
});

export function RuntimeBlindSpotsCard({ summary, onViewAll }: RuntimeBlindSpotsCardProps) {
  const styles = useStyles();
  const scoreColor = getScoreColor(summary.rbssScore);

  return (
    <Card>
      <CardHeader
        image={<EyeOffRegular style={{ fontSize: '20px', color: '#E85252' }} />}
        header={<Text weight="semibold">Runtime Blind Spots</Text>}
        description={<Body2>RBSS Score - {summary.totalActive} active</Body2>}
      />
      <div className={styles.scoreSection}>
        <Badge appearance="filled" color={scoreColor} size="large">
          {summary.rbssScore} / 100
        </Badge>
        <div className={styles.severityCounts}>
          {summary.severityCounts.critical > 0 && (
            <Badge appearance="tint" color="danger" size="medium">
              {summary.severityCounts.critical} Critical
            </Badge>
          )}
          {summary.severityCounts.high > 0 && (
            <Badge appearance="tint" color="important" size="medium">
              {summary.severityCounts.high} High
            </Badge>
          )}
          {summary.severityCounts.medium > 0 && (
            <Badge appearance="tint" color="warning" size="medium">
              {summary.severityCounts.medium} Medium
            </Badge>
          )}
          {summary.severityCounts.low > 0 && (
            <Badge appearance="tint" color="informative" size="medium">
              {summary.severityCounts.low} Low
            </Badge>
          )}
        </div>
      </div>
      <Divider />
      <div className={styles.topItems}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', padding: `${tokens.spacingVerticalXS} 0` }}>
          Top Blind Spots
        </Caption1>
        {summary.topBlindSpots.slice(0, 3).map((bs) => (
          <div key={bs.id} className={styles.topItem}>
            <Body2>{formatBlindSpotType(bs.blindSpotType)}: {bs.scopeId}</Body2>
            <Badge appearance="tint" color={getSeverityBadgeColor(bs.severity)} size="medium">
              {bs.severity}
            </Badge>
          </div>
        ))}
        {summary.topBlindSpots.length === 0 && (
          <Caption1 style={{ color: tokens.colorNeutralForeground3, padding: `${tokens.spacingVerticalS} 0`, display: 'block' }}>
            No active blind spots detected.
          </Caption1>
        )}
      </div>
      <div className={styles.footer}>
        <Button
          appearance="subtle"
          icon={<ArrowRightRegular />}
          iconPosition="after"
          onClick={onViewAll}
        >
          View all blind spots
        </Button>
      </div>
    </Card>
  );
}
